import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { ArrowLeft, CheckCircle2, AlertTriangle, PenLine, RotateCcw } from "lucide-react";

function money(v) { return `$${Number(v || 0).toFixed(2)}`; }
function humanTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function FloatDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [tx, setTx] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewComments, setReviewComments] = useState("");
  const canReview = hasAccess(user?.role, ["admin", "gm"]);
  const justRecorded = location.state?.justRecorded;

  const load = useCallback(() => {
    api.get(`/float_changes/${id}`).then(({ data }) => setTx(data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const markReviewed = async () => {
    try {
      await api.post(`/float_changes/${id}/review`, { comments: reviewComments });
      toast.success("Marked as reviewed");
      setReviewing(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!tx) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const isBalanced = Math.abs(Number(tx.difference || 0)) < 0.005;
  const cls = isBalanced ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/40" : "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40";

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="float-detail-page">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      {justRecorded && (
        <div className="hos-card p-4 border border-emerald-400/50 bg-emerald-500/10 text-emerald-300 flex items-center gap-2">
          <CheckCircle2 size={20}/> Float change recorded permanently.
        </div>
      )}

      <div className="hos-card p-6 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="label-caps">Reference</div>
            <div className="font-heading text-2xl text-[color:var(--gold)]">{tx.reference_number}</div>
            <div className="text-xs text-[color:var(--text-secondary)]">{humanTime(tx.created_at)}</div>
          </div>
          <span className={`text-xs px-3 py-1.5 rounded-full border uppercase ${cls}`} data-testid="detail-status">{tx.status}</span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="label-caps">Store</div>{tx.store_name || "—"}</div>
          <div><div className="label-caps">Register</div>{tx.register_name}</div>
          <div><div className="label-caps">Manager</div>{tx.manager_name}</div>
          <div><div className="label-caps">Confirmed</div>{tx.manager_confirmed ? "Yes" : "No"}</div>
        </div>

        <LineTable title="Taken from Register" lines={tx.taken_lines} total={tx.taken_total}/>
        <LineTable title="Replaced With" lines={tx.replacement_lines} total={tx.replacement_total}/>

        <div className={`p-3 rounded ${isBalanced ? "bg-emerald-500/10 text-emerald-300" : "bg-[color:var(--warning)]/10 text-[color:var(--warning)]"}`}>
          <div className="flex justify-between font-heading text-lg">
            <span>Difference</span>
            <span>{money(tx.difference)}</span>
          </div>
          {!isBalanced && tx.mismatch_reason && <div className="text-sm mt-1"><b>Reason:</b> {tx.mismatch_reason}</div>}
        </div>

        {tx.notes && <div><div className="label-caps">Notes</div><div className="text-sm">{tx.notes}</div></div>}

        {tx.is_correction && tx.original && (
          <div className="p-3 rounded border border-purple-400/50 bg-purple-500/10 text-purple-200 text-sm">
            <div className="flex items-center gap-2 font-semibold mb-1"><RotateCcw size={14}/> This is a correction of <Link to={`/float/${tx.original.id}`} className="text-[color:var(--gold)] underline">{tx.original.reference_number}</Link></div>
            <div><b>Reason:</b> {tx.correction_reason}</div>
          </div>
        )}
        {tx.has_correction && (tx.corrections || []).length > 0 && (
          <div className="p-3 rounded border border-purple-400/50 bg-purple-500/10 text-purple-200 text-sm">
            <div className="font-semibold mb-1">Correction(s):</div>
            {(tx.corrections || []).map((c) => (
              <Link key={c.id} to={`/float/${c.id}`} className="block text-[color:var(--gold)] underline">
                {c.reference_number} · {humanTime(c.created_at)}
              </Link>
            ))}
          </div>
        )}

        {tx.reviewed_by_name && (
          <div className="text-xs text-[color:var(--text-secondary)]">
            Reviewed by {tx.reviewed_by_name} on {humanTime(tx.reviewed_at)}
            {tx.review_comments && <div className="italic">"{tx.review_comments}"</div>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 justify-end">
        {!tx.is_correction && !tx.has_correction && (
          <Link to={`/float/new?correctionOf=${tx.id}`} onClick={(e) => { e.preventDefault(); nav(`/float/correction/${tx.id}`); }} className="hos-btn-outline flex items-center gap-2" data-testid="create-correction-btn">
            <PenLine size={14}/> Create Correction
          </Link>
        )}
        {canReview && !isBalanced && tx.status !== "reviewed" && (
          <button className="hos-btn-primary flex items-center gap-2" onClick={() => setReviewing(true)} data-testid="mark-reviewed-btn">
            <CheckCircle2 size={14}/> Mark Reviewed
          </button>
        )}
      </div>

      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setReviewing(false)}>
          <div className="hos-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-heading text-2xl mb-3">Mark as Reviewed</div>
            <textarea className="hos-input min-h-[80px]" placeholder="Optional review comments…" value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} data-testid="review-comments"/>
            <div className="flex justify-end gap-3 mt-4">
              <button className="hos-btn-outline" onClick={() => setReviewing(false)}>Cancel</button>
              <button className="hos-btn-primary" onClick={markReviewed} data-testid="review-confirm">Confirm Review</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LineTable({ title, lines, total }) {
  return (
    <div>
      <div className="label-caps mb-1">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {(lines || []).map((l, i) => (
            <tr key={i} className="border-t border-[color:var(--border)]">
              <td className="py-1">{l.quantity} × {l.denomination_name}</td>
              <td className="text-right text-[color:var(--gold)]">{money(l.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[color:var(--border)] font-heading">
            <td className="py-2">Total</td>
            <td className="text-right">{money(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
